import React, { useState, useEffect } from "react";
import axios from "axios";
import { Instagram, Loader2, Plus, Trash2, ExternalLink, GripVertical, Star, RefreshCw, Link2, Unplug, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AutoSyncCard({ authHeaders, toast }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/instagram/status`, { headers: authHeaders });
      setStatus(data);
    } catch { /* panel still usable without status */ }
  };

  useEffect(() => {
    loadStatus();
    const result = new URLSearchParams(window.location.search).get("instagram");
    if (result === "connected") {
      toast({ title: "Instagram connected", description: "Your latest posts are now syncing to the home page automatically." });
      window.history.replaceState({}, "", "/admin?tab=instagram");
    } else if (result === "error") {
      toast({ title: "Instagram connection failed", description: "Please try again — make sure you log in with the restaurant's account." });
      window.history.replaceState({}, "", "/admin?tab=instagram");
    }
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/instagram/connect-url`, {
        params: { origin: window.location.origin },
        headers: authHeaders,
      });
      window.location.href = data.url;
    } catch (e) {
      toast({ title: "Couldn't start connection", description: e?.response?.data?.detail || e.message });
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/instagram/sync`, {}, { headers: authHeaders });
      toast({ title: "Synced", description: `Pulled ${data.synced} latest posts from Instagram.` });
      loadStatus();
    } catch (e) {
      toast({ title: "Sync failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? The home page will fall back to your hand-curated posts.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/instagram/disconnect`, {}, { headers: authHeaders });
      toast({ title: "Disconnected", description: "Auto-sync is off — curated posts are showing again." });
      loadStatus();
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div className="p-5 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)]" data-testid="ig-autosync-card">
      <div className="section-label mb-2 flex items-center gap-2">
        <Zap size={14} /> Auto-sync
      </div>
      {!status.configured ? (
        <p className="text-sm text-[var(--mm-cream-muted)] leading-relaxed">
          Waiting for your Meta App ID &amp; Secret to be added — once they&apos;re in, a
          &ldquo;Connect Instagram&rdquo; button appears here and your latest posts will
          pull through automatically every hour.
        </p>
      ) : status.connected ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--mm-cream)]">
            Connected as <span className="text-[var(--mm-accent)]">@{status.account?.username}</span>
            <span className="text-[var(--mm-cream-muted)]">
              {" "}· {status.account?.synced_count || 0} posts synced
              {status.account?.last_synced ? ` · last sync ${new Date(status.account.last_synced).toLocaleString("en-AU")}` : ""}
            </span>
          </p>
          {status.account?.last_error && (
            <p className="text-xs text-red-400">Last sync error: {status.account.last_error}</p>
          )}
          <div className="flex flex-wrap gap-3">
            <button onClick={syncNow} disabled={busy} data-testid="ig-sync-now" className="btn-outline inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Sync now
            </button>
            <button onClick={disconnect} disabled={busy} data-testid="ig-disconnect" className="inline-flex items-center gap-2 text-[0.68rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 px-3">
              <Unplug size={13} /> Disconnect
            </button>
          </div>
          <p className="text-xs text-[var(--mm-cream-muted)]">
            The home page refreshes from Instagram automatically every hour. Your curated posts below stay as a fallback,
            and a &ldquo;featured&rdquo; curated post still wins the hero slot.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[var(--mm-cream-muted)] leading-relaxed">
            Tap connect and log in with the restaurant&apos;s Instagram — after that the site pulls
            your latest posts automatically every hour and renews itself.
          </p>
          <button onClick={connect} disabled={busy} data-testid="ig-connect" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Connect Instagram
          </button>
        </div>
      )}
    </div>
  );
}

export default function InstagramPanel({ authHeaders, toast }) {
  const [items, setItems] = useState([]);
  const [handle, setHandle] = useState("mamaminasoulfeed_");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ image_url: "", post_url: "", caption: "", order: 0 });

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/instagram/posts`, { headers: authHeaders });
      setItems(data.items || []);
      if (data.handle) setHandle(data.handle);
    } catch (e) {
      toast({ title: "Load failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.image_url.trim()) {
      toast({ title: "Image URL required", description: "Right-click the Instagram image and copy the URL." });
      return;
    }
    try {
      await axios.post(`${API}/admin/instagram/posts`, {
        image_url: form.image_url.trim(),
        post_url: form.post_url.trim() || null,
        caption: form.caption.trim() || null,
        order: Number(form.order) || 0,
        active: true,
      }, { headers: authHeaders });
      setForm({ image_url: "", post_url: "", caption: "", order: 0 });
      toast({ title: "Post added", description: "Now live on the home page." });
      load();
    } catch (e) {
      toast({ title: "Add failed", description: e?.response?.data?.detail || e.message });
    }
  };

  const update = async (post, patch) => {
    // Optimistic — enforce single-featured on the client to match backend behaviour.
    setItems((arr) => arr.map((p) => {
      if (p.id === post.id) return { ...p, ...patch };
      if (patch.featured === true) return { ...p, featured: false };
      return p;
    }));
    try {
      await axios.put(`${API}/admin/instagram/posts/${post.id}`, patch, { headers: authHeaders });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message });
      load();
    }
  };

  const toggleFeatured = (post) => {
    update(post, { featured: !post.featured });
    toast({
      title: !post.featured ? "Post featured" : "Feature cleared",
      description: !post.featured
        ? "This post now renders as a hero above the grid."
        : "The Instagram section is back to a plain grid.",
    });
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete this post? "${(post.caption || "").slice(0, 40)}"`)) return;
    try {
      await axios.delete(`${API}/admin/instagram/posts/${post.id}`, { headers: authHeaders });
      toast({ title: "Deleted", description: "Post removed from the grid." });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-6">
      <AutoSyncCard authHeaders={authHeaders} toast={toast} />

      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
        <div className="section-label mb-2 flex items-center gap-2">
          <Instagram size={14} /> Instagram Feed — @{handle}
        </div>
        <p className="text-sm text-[var(--mm-cream-muted)] leading-relaxed">
          Curate what returning diners see on the home page. Paste the image URL of an Instagram post, the post link, and a short caption.
          To grab the image URL: on the Instagram post, right-click the photo → &ldquo;Copy image address&rdquo; (desktop) or use a tool like <a href="https://instasupersave.com/en/download-photo/" target="_blank" rel="noreferrer" className="text-[var(--mm-accent)] underline">instasupersave</a>.
        </p>
      </div>

      {/* Add new */}
      <div className="p-5 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)]">
        <div className="section-label mb-4 flex items-center gap-2"><Plus size={14} /> Add a new post</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            type="text"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="Image URL (required)"
            data-testid="ig-form-image-url"
            className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
          />
          <input
            type="text"
            value={form.post_url}
            onChange={(e) => setForm({ ...form, post_url: e.target.value })}
            placeholder="Instagram post URL (optional)"
            data-testid="ig-form-post-url"
            className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
          />
          <input
            type="text"
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder="Caption (shown on hover)"
            maxLength={500}
            data-testid="ig-form-caption"
            className="md:col-span-2 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
          />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-[var(--mm-cream-muted)]">
            Order:
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
              className="w-16 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 focus:border-[var(--mm-accent)] outline-none"
            />
            <span className="text-[0.62rem] tracking-[0.2em] uppercase">lower = first</span>
          </div>
          <button
            onClick={create}
            data-testid="ig-form-submit"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14} /> Add post
          </button>
        </div>
      </div>

      {/* Existing posts */}
      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-[var(--mm-cream-muted)] font-light border border-dashed border-[var(--mm-line)]">
          No Instagram posts yet. Add one above and it will appear on the home page.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`ig-admin-tile-${p.id}`}
              className={`
                relative border bg-[var(--mm-bg-alt)] p-3 space-y-3 transition-colors
                ${p.featured ? "border-[var(--mm-accent)] shadow-[0_0_0_1px_var(--mm-accent)_inset]" : "border-[var(--mm-line)]"}
              `}
            >
              <button
                onClick={() => toggleFeatured(p)}
                data-testid={`ig-featured-${p.id}`}
                title={p.featured ? "Remove as featured hero" : "Feature this post as the hero"}
                aria-pressed={!!p.featured}
                className={`
                  absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center
                  transition-all duration-200 backdrop-blur-sm
                  ${p.featured
                    ? "bg-[var(--mm-accent)] text-black hover:brightness-110"
                    : "bg-black/60 text-white/70 hover:text-[var(--mm-accent)] hover:bg-black/80"}
                `}
              >
                <Star size={14} className={p.featured ? "fill-black" : ""} />
              </button>
              {p.featured && (
                <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[var(--mm-accent)] text-black text-[0.55rem] tracking-[0.22em] uppercase font-medium">
                  Featured
                </div>
              )}
              <div className="aspect-square overflow-hidden border border-[var(--mm-line)]">
                <img src={p.image_url} alt="ig" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              <input
                defaultValue={p.image_url}
                onBlur={(e) => { if (e.target.value !== p.image_url) update(p, { image_url: e.target.value }); }}
                placeholder="Image URL"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none"
              />
              <input
                defaultValue={p.post_url || ""}
                onBlur={(e) => { if (e.target.value !== (p.post_url || "")) update(p, { post_url: e.target.value }); }}
                placeholder="Post URL"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none"
              />
              <textarea
                defaultValue={p.caption || ""}
                onBlur={(e) => { if (e.target.value !== (p.caption || "")) update(p, { caption: e.target.value }); }}
                placeholder="Caption"
                rows={2}
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none resize-none"
              />
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-[var(--mm-cream-muted)]">
                  <GripVertical size={12} />
                  <input
                    type="number"
                    defaultValue={p.order}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== p.order) update(p, { order: v }); }}
                    className="w-14 bg-transparent border border-[var(--mm-line)] px-1 text-[var(--mm-cream)] focus:border-[var(--mm-accent)] outline-none"
                  />
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!p.active}
                    onChange={(e) => update(p, { active: e.target.checked })}
                    className="sr-only peer"
                    data-testid={`ig-active-${p.id}`}
                  />
                  <span className={`w-9 h-5 rounded-full transition-colors relative ${p.active ? "bg-[var(--mm-accent)]" : "bg-[var(--mm-line)]"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${p.active ? "translate-x-4" : ""}`} />
                  </span>
                  <span className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">{p.active ? "Live" : "Hidden"}</span>
                </label>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[var(--mm-line)]/60">
                {p.post_url ? (
                  <a href={p.post_url} target="_blank" rel="noreferrer" className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-accent)] inline-flex items-center gap-1">
                    <ExternalLink size={11} /> View
                  </a>
                ) : <span />}
                <button
                  onClick={() => remove(p)}
                  data-testid={`ig-delete-${p.id}`}
                  className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 inline-flex items-center gap-1"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
